import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import type { LadderBand } from '../../types/sniper.types';
import {
  LADDER_BAND_TEMPLATES,
  MAX_LADDER_BANDS,
  defaultLadderBands,
  gatePct,
  ladderInvalidReason,
  ladderSummary,
  normalizeDecimalInput,
} from '../../utils/sniperArm';

/** The price configuration of a market: the rungs, the floor and the VAR offset. */
export interface SniperConfigDraft {
  ladderBands: LadderBand[];
  /** Dollars, or `null` while unset (required as soon as anything can sell). */
  floorPrice: number | null;
  /**
   * Dollars below our own buy vwap the "VAR under review" sell rests at.
   * `null` = unset, which the engine reads as its own 2¢ default — so CLEARING
   * the cell is a real state the field must be able to show. Coalescing it back
   * to a number in the handler left `value` unchanged, so the sync effect never
   * re-ran and the input stayed visually blank over a draft that held 0.02.
   */
  varPreventiveExitOffset: number | null;
}

/**
 * Which fields the operator actually changed in this editing session, reported
 * alongside the draft on Apply. A bulk caller writes ONLY the touched ones: the
 * floor is inherently per-market (it has to sit below that market's pre-event
 * price) and the offset is often deliberately tuned, so stamping either across a
 * selection because the operator came in to add a rung would silently retune
 * markets they never looked at.
 */
export interface SniperConfigTouched {
  ladderBands: boolean;
  floorPrice: boolean;
  varPreventiveExitOffset: boolean;
}

/** How far above a rung's buy a restored sell is seeded, in dollars. */
const SELL_RESTORE_OFFSET = 0.02;

/** The sell a rung gets back when its Sell checkbox is re-ticked. */
function restoredSellPrice(buyPrice: number, remembered?: number): number {
  if (remembered != null) return remembered;
  return Math.min(1, Number((buyPrice + SELL_RESTORE_OFFSET).toFixed(2)));
}

/**
 * The market's price configuration, in a popover: the confidence-ladder rung
 * table, the single dump floor and the VAR-under-review offset. This is the ONLY
 * place prices are set — there is no competing one-shot model.
 *
 * It lives in a popover rather than inline because a ladder is a TABLE: the
 * rungs only make sense read together, with their ordering and anti-self-cross
 * rules visible at once, which fits neither the market row nor the import
 * dialog's row density.
 *
 * Editing is staged: `value` seeds a local draft, Apply hands the whole draft
 * back, Cancel throws it away. Apply is disabled while the ladder is invalid, so
 * the operator sees the exact rule they broke instead of a 400.
 */
export function SniperConfigPopover({
  value,
  onApply,
  disabled,
  /** Rendered instead of the default chip (the bulk/import bars pass a button). */
  renderTrigger,
  /**
   * Whether a floor MUST be set — true iff the row sells on VAR or on an
   * annulled goal. With both switches off nothing ever dumps to the floor.
   */
  floorRequired = true,
  /**
   * Whether an EMPTY floor can actually reach the wire. `false` on the per-row
   * patch path: `PatchSniperMarketDto.floorPrice` has no way to express "unset",
   * so a cleared floor would simply be omitted and the refetch would silently
   * restore it. Create (`markets` DTO) and the bulk bars CAN express it — an
   * absent floor there means "keep each market's own" — so they leave it true.
   *
   * The cell itself always stays editable: the floor is range-checked even when
   * it is not required, so disabling it would strand an operator who lowered a
   * rung under a stored floor with a permanently disabled Apply and no way to
   * reach the offending value.
   */
  floorClearable = true,
}: {
  value: SniperConfigDraft;
  onApply: (next: SniperConfigDraft, touched: SniperConfigTouched) => void;
  disabled?: boolean;
  renderTrigger?: (open: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
  floorRequired?: boolean;
  floorClearable?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<SniperConfigDraft>(value);
  // The sell each rung carried before its Sell checkbox was cleared, so
  // re-ticking restores the operator's own price instead of a guessed one.
  const [rememberedSell, setRememberedSell] = useState<Record<number, number>>({});
  const open = Boolean(anchorEl);

  // Re-seed on every OPEN, not on every `value` change: a server refetch while
  // the popover is open must not wipe what the operator is typing.
  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setRememberedSell({});
    setAnchorEl(e.currentTarget);
  };
  useEffect(() => {
    if (!open) setDraft(value);
  }, [open, value]);

  const invalid = useMemo(() => {
    // Clearing a floor this path cannot express would look applied and then be
    // undone by the refetch — name the transition instead of silently dropping
    // it. Typing any value (or Cancel) escapes, so this is never a dead end.
    if (!floorClearable && value.floorPrice != null && draft.floorPrice == null) {
      return 'the floor cannot be cleared — set a value or Cancel';
    }
    return ladderInvalidReason(draft.ladderBands, draft.floorPrice, {
      varPreventiveExitOffset: draft.varPreventiveExitOffset,
      floorRequired,
    });
  }, [
    draft.ladderBands,
    draft.floorPrice,
    draft.varPreventiveExitOffset,
    floorRequired,
    floorClearable,
    value.floorPrice,
  ]);

  // No rungs at all = the market is UNCONFIGURED (the migration leaves `[]`
  // where no lossless mapping existed). It cannot trade, and the summary must
  // say so instead of reading like a configured table.
  const unconfigured = value.ladderBands.length === 0;

  const setBand = (i: number, patch: Partial<LadderBand>) =>
    setDraft((d) => ({
      ...d,
      ladderBands: d.ladderBands.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    }));

  const toggleSell = (i: number, sells: boolean) => {
    const band = draft.ladderBands[i];
    if (!band) return;
    if (sells) {
      setBand(i, { sellPrice: restoredSellPrice(band.buyPrice, rememberedSell[i]) });
      return;
    }
    if (band.sellPrice != null) {
      const remembered = band.sellPrice;
      setRememberedSell((prev) => ({ ...prev, [i]: remembered }));
    }
    setBand(i, { sellPrice: null });
  };

  const addBand = () =>
    setDraft((d) => {
      const last = d.ladderBands[d.ladderBands.length - 1];
      // Seed the new rung one cent above the last one so it starts VALID —
      // the operator adjusts from a working ladder instead of a broken one.
      // Capped at 1 (the validator's own (0,1] bound), NOT 0.99 (Kalshi's
      // tradeable ceiling, which the engine clamps separately at execution
      // time) — a last rung already at 0.99 would otherwise seed an
      // IDENTICAL price, which the "must increase" rule then rejects on
      // arrival. A non-selling last rung seeds a non-selling one.
      const next: LadderBand = last
        ? {
            maxConfidence: Math.min(1, Number((last.maxConfidence + 0.2).toFixed(2))),
            buyPrice: Math.min(1, Number((last.buyPrice + 0.01).toFixed(2))),
            sellPrice:
              last.sellPrice == null
                ? null
                : Math.min(1, Number((last.sellPrice + 0.01).toFixed(2))),
          }
        : defaultLadderBands()[0];
      return { ...d, ladderBands: [...d.ladderBands, next] };
    });

  const removeBand = (i: number) =>
    setDraft((d) => ({
      ...d,
      ladderBands: d.ladderBands.filter((_, j) => j !== i),
    }));

  // ── V1 rollout: rungs 2 and 3 are individually enable/disable-able ──
  //
  // The wire has no per-rung "enabled" flag — a rung is enabled iff the array
  // reaches its index, and the array is always contiguous (rung 3 cannot
  // exist without rung 2). So "enable rung i+1" appends its TEMPLATE, and
  // "disable rung i+1" truncates the array back to i — dropping it and any
  // rung after it, since a later rung can never outlive the one that gates it.
  const enableNextTemplatedRung = () =>
    setDraft((d) => {
      const i = d.ladderBands.length;
      const template = LADDER_BAND_TEMPLATES[i];
      if (!template) return d; // already past the 3 templated rungs
      return { ...d, ladderBands: [...d.ladderBands, { ...template }] };
    });
  const disableRungsFrom = (i: number) =>
    setDraft((d) => ({ ...d, ladderBands: d.ladderBands.slice(0, i) }));

  const apply = () => {
    if (invalid) return;
    onApply(draft, {
      // Compared against the value this session was SEEDED with, so re-typing
      // the same number is not a change and an untouched field is never written.
      ladderBands:
        JSON.stringify(draft.ladderBands) !== JSON.stringify(value.ladderBands),
      floorPrice: draft.floorPrice !== value.floorPrice,
      varPreventiveExitOffset:
        draft.varPreventiveExitOffset !== value.varPreventiveExitOffset,
    });
    setAnchorEl(null);
  };

  /** The four price cells of one rung (confidence · buy · sell switch · sell). */
  const bandCells = (band: LadderBand, i: number) => (
    <>
      <PctCell
        value={band.maxConfidence}
        onChange={(maxConfidence) => setBand(i, { maxConfidence })}
        ariaLabel={`band ${i + 1} max confidence`}
        // The last rung is open-ended: anything at or above its threshold
        // stays on it, so the number is only a boundary.
        hint={i === draft.ladderBands.length - 1 ? 'open-ended above this' : undefined}
      />
      {/* A rung's buy is never absent, so a cleared field reads as 0 — out of
          (0,1], which the validation then names. */}
      <PriceCell
        value={band.buyPrice}
        onChange={(v) => setBand(i, { buyPrice: v ?? 0 })}
        ariaLabel={`band ${i + 1} buy price`}
      />
      <Tooltip title="Sell at this rung — untick to hold the position through it">
        <Checkbox
          size="small"
          checked={band.sellPrice != null}
          onChange={(e) => toggleSell(i, e.target.checked)}
          inputProps={{ 'aria-label': `band ${i + 1} sell enabled` }}
          sx={{ p: 0.25, width: 34 }}
        />
      </Tooltip>
      <PriceCell
        value={band.sellPrice}
        onChange={(v) => setBand(i, { sellPrice: v })}
        ariaLabel={`band ${i + 1} sell price`}
        disabled={band.sellPrice == null}
      />
    </>
  );

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openPopover)
      ) : (
        <Tooltip
          title={
            unconfigured
              ? 'This market has NO ladder — it cannot trade until you set the rungs. Nothing here was defaulted for you.'
              : 'Ladder config — the rungs, the floor and the VAR offset'
          }
        >
          <Chip
            size="small"
            icon={<TuneIcon sx={{ fontSize: 12 }} />}
            label={
              unconfigured
                ? 'no ladder — configure'
                : ladderSummary(value.ladderBands, value.floorPrice)
            }
            onClick={openPopover}
            disabled={disabled}
            aria-label="market config"
            sx={{
              // An unconfigured market is not a styling detail: it cannot trade,
              // and the row's ✓ stays disabled until it is fixed.
              bgcolor: unconfigured ? 'warning.main' : '#8e5bf5',
              color: unconfigured ? 'rgba(0,0,0,0.87)' : '#fff',
              fontWeight: 700,
              height: 20,
              fontSize: 10,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
            }}
          />
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 400 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: 12.5, fontWeight: 700, color: 'common.white', mb: 0.75 }}
          >
            Confidence ladder
          </Typography>

          <Stack direction="row" spacing={0.75} sx={{ mb: 0.5, px: 0.25 }}>
            <HeaderCell width={72} label="Up to %" />
            <HeaderCell width={72} label="Buy $" />
            <HeaderCell width={34} label="Sell" />
            <HeaderCell width={72} label="Sell $" />
          </Stack>
          <Stack spacing={0.75}>
            {/* Rungs 1–3: V1 rollout ships rung 1 only, with 2 and 3
                individually enabled later. Rung 1 has no toggle — the ladder
                can never run with zero rungs. */}
            {[0, 1, 2].map((i) => {
              const band = draft.ladderBands[i];
              if (band) {
                return (
                  <Stack
                    // Rungs have no identity of their own — the index IS the
                    // rung, and the array is rewritten whole on every edit.
                    key={i}
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                  >
                    {bandCells(band, i)}
                    {i === 0 ? (
                      <Box sx={{ width: 34 }} />
                    ) : (
                      <Tooltip title={`Disable rung ${i + 1}`}>
                        <Switch
                          size="small"
                          checked
                          onChange={() => disableRungsFrom(i)}
                          inputProps={{ 'aria-label': `band ${i + 1} enabled` }}
                          sx={{ ml: -0.5 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                );
              }
              // No rung at this index yet — a collapsed row offering just the
              // toggle. Only the IMMEDIATE next rung is enableable (rung 3
              // needs rung 2 first), so it's disabled otherwise.
              const isNext = i === draft.ladderBands.length;
              return (
                <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: 11, flex: 1 }}
                  >
                    Rung {i + 1} — off
                  </Typography>
                  <Tooltip
                    title={isNext ? `Enable rung ${i + 1}` : `Enable rung ${i} first`}
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={false}
                        disabled={!isNext}
                        onChange={() => isNext && enableNextTemplatedRung()}
                        inputProps={{ 'aria-label': `band ${i + 1} enabled` }}
                        sx={{ ml: -0.5 }}
                      />
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}

            {/* Beyond the 3 templated rungs — the free-form add/remove, for an
                operator who wants more than the V1 default 3. */}
            {draft.ladderBands.slice(3).map((band, j) => {
              const i = j + 3;
              return (
                <Stack key={i} direction="row" spacing={0.75} alignItems="center">
                  {bandCells(band, i)}
                  <IconButton
                    size="small"
                    onClick={() => removeBand(i)}
                    aria-label={`remove band ${i + 1}`}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>

          {draft.ladderBands.length >= 3 && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={addBand}
              disabled={draft.ladderBands.length >= MAX_LADDER_BANDS}
              aria-label="add band"
              sx={{ mt: 0.5, fontSize: 11 }}
            >
              Add band
            </Button>
          )}

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={0.75}>
            <Tooltip title="How far down the position is dumped when the goal falls over — it has to sit BELOW the market's pre-event price">
              <span>
                <PriceCell
                  label="Floor $"
                  width={90}
                  value={draft.floorPrice}
                  onChange={(floorPrice) => setDraft((d) => ({ ...d, floorPrice }))}
                  ariaLabel="ladder floor price"
                />
              </span>
            </Tooltip>
            <Tooltip title="Dollars below our own buy vwap the VAR-under-review sell rests at — a small controlled loss aggressive enough to actually fill while the market reprices under review">
              <span>
                <PriceCell
                  label="VAR offset $"
                  width={90}
                  value={draft.varPreventiveExitOffset}
                  onChange={(varPreventiveExitOffset) =>
                    setDraft((d) => ({ ...d, varPreventiveExitOffset }))
                  }
                  ariaLabel="ladder var preventive exit offset"
                />
              </span>
            </Tooltip>
          </Stack>

          {invalid && (
            <Typography
              variant="caption"
              sx={{ color: 'error.main', fontSize: 10.5, display: 'block', mt: 1 }}
            >
              {invalid}
            </Typography>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => setAnchorEl(null)}
              aria-label="cancel market config"
              sx={{ fontSize: 11 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              disabled={!!invalid}
              aria-label="apply market config"
              sx={{ fontSize: 11 }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

function HeaderCell({ width, label }: { width: number; label: string }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width, fontSize: 9.5, lineHeight: 1 }}
    >
      {label}
    </Typography>
  );
}

const cellSx = {
  '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
  '& .MuiInputLabel-root': { fontSize: 10.5 },
  '& .MuiInputLabel-shrink': { fontSize: 11 },
};

/**
 * A dollar price cell. Kept as free TEXT while editing (so `0.` and a cleared
 * field survive a keystroke) and reported as a number — `null` when the field is
 * empty or not a number, which the ladder validation then names.
 */
function PriceCell({
  value,
  onChange,
  ariaLabel,
  label,
  width = 72,
  disabled,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  label?: string;
  width?: number;
  disabled?: boolean;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    setText((prev) => (Number(prev) === value ? prev : value == null ? '' : String(value)));
  }, [value]);
  return (
    <TextField
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      onChange={(e) => {
        const next = normalizeDecimalInput(e.target.value);
        setText(next);
        onChange(next.trim() === '' || !Number.isFinite(Number(next)) ? null : Number(next));
      }}
      inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
      sx={{ width, ...cellSx }}
    />
  );
}

/** A confidence cell, edited as an INTEGER PERCENT over a 0..1 stored value. */
function PctCell({
  value,
  onChange,
  ariaLabel,
  hint,
}: {
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
  hint?: string;
}) {
  const [text, setText] = useState(String(gatePct(value)));
  useEffect(() => {
    setText((prev) => (Number(prev) === gatePct(value) ? prev : String(gatePct(value))));
  }, [value]);
  return (
    <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
      <TextField
        size="small"
        value={text}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9]/g, '');
          setText(next);
          // Empty reads as 0, which the validation rejects as out of (0,1] —
          // the operator sees "confidence 0–100%" rather than a silent default.
          onChange(next === '' ? 0 : Number(next) / 100);
        }}
        inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': ariaLabel }}
        sx={{ width: 72, ...cellSx }}
      />
    </Tooltip>
  );
}
